<template>
  <div class="c-proposal-task">
    <h4 class="c-proposal-task__title">
      このタスクを完了した時にチェックする項目
    </h4>
    <div class="c-proposal-task__content">
      <p>
        <Icon2 variant="check-circle" :size="15" />
        <template v-if="isChangeTd">
          「{{
            proposal.keyword
          }}」のキーワードのクリック率に変化があるかを確認する
        </template>
        <template v-else-if="isRewrite">
          「{{ proposal.keyword }}」のキーワードの順位に変化があるかを確認する
        </template>
        <template v-else-if="isSeoCheck">
          <ProposalTaskSeoIssues :proposal="proposal" />
        </template>
      </p>
    </div>
    <div class="c-fieldset c-fieldset--wrap">
      <div class="c-fieldset__header">
        <p class="c-fieldset__title">タスクに担当者を設定する</p>
      </div>
      <div class="c-fieldset__content c-alertPos">
        <Select
          v-model="params.operator"
          :options="projectUsers"
          placeholder="担当者なし"
          :selected="params.operator"
          size="sm"
          :class="{
            alert: error.operator
          }"
        />
        <Alert v-if="error.operator">
          {{ error.operator }}
        </Alert>
      </div>
    </div>
    <div class="c-proposal-task__footer">
      <div class="c-alertPos">
        <Alert v-if="errorResponse.reject">
          {{ errorResponse.reject }}
        </Alert>
        <Button
          fsize="f16"
          variant="darkgray"
          class="l-w-180"
          :class="{ 'is-loading': isProcessing.reject }"
          :disabled="isDisabled"
          @click="rejectHint"
        >
          今回はしない
        </Button>
      </div>
      <div class="c-alertPos">
        <Alert v-if="errorResponse.accept">
          {{ errorResponse.accept }}
        </Alert>
        <Button
          fsize="f16"
          variant="main"
          icon="plus"
          class="l-w-250"
          :class="{ 'is-loading': isProcessing.accept }"
          :disabled="isDisabled || isError"
          @click="acceptHint"
        >
          タスクに登録する
        </Button>
      </div>
    </div>
  </div>
</template>

<script>
import { ERROR_MESSAGE } from '@/constants';
import Alert from '@/components/Alert';
import Button from '@/components/Button';
import Icon2 from '@/components/Icon2';
import ProposalTaskSeoIssues from '@/components/proposal/ProposalTaskSeoIssues';
import Select from '@/components/Select';
import apiProjects from '@/api/projects';
import utils from '@/utils';
export default {
  props: {
    project: {
      type: Object,
      required: true
    },
    proposal: {
      type: Object,
      required: false
    }
  },
  components: {
    Alert,
    Button,
    Icon2,
    ProposalTaskSeoIssues,
    Select
  },
  data() {
    return {
      error: {
        operator: ''
      },
      errorResponse: {
        accept: '',
        reject: ''
      },
      params: {
        operator: ''
      },
      isEdited: {
        operator: false
      },
      isProcessing: {
        accept: false,
        reject: false
      }
    };
  },
  computed: {
    projectUsers() {
      return this.$store.getters['project/usersSelect'];
    },
    isChangeTd() {
      return this.proposal.type === 'changeTd';
    },
    isDisabled() {
      return this.isProcessing.accept || this.isProcessing.reject;
    },
    isError() {
      return this.error.operator ? true : false;
    },
    isRewrite() {
      return this.proposal.type === 'rewrite';
    },
    isSeoCheck() {
      return this.proposal.type === 'seoCheckUp';
    }
  },
  watch: {
    params: {
      handler() {
        // 何か入力したらサーバーからのエラーメッセージを消す
        this.resetError();
      },
      deep: true
    },
    'params.operator': {
      handler(value) {
        const operator = value;
        this.error.operator = '';
        this.isEdited.operator = operator ? true : false;
      }
    }
  },
  methods: {
    async acceptHint() {
      if (this.isDisabled || this.isError) {
        return;
      }
      this.isProcessing.accept = true;
      this.resetError();

      // 利用量チェック
      await this.getUsages();
      if (this.errorResponse.accept) {
        this.isProcessing.accept = false;
        return;
      }
      // ユーザーの存在チェック
      if (this.params.operator) {
        await this.getUsers();
        if (this.error.operator) {
          this.isProcessing.accept = false;
          return;
        }
      }
      // ログ送信
      await this.postLog('accept');
      if (this.errorResponse.accept) {
        this.isProcessing.accept = false;
        return;
      }
      // タスク登録
      await this.postTask(this.proposal);

      this.isProcessing.accept = false;
    },
    async rejectHint() {
      if (this.isDisabled) {
        return;
      }
      this.isProcessing.reject = true;
      this.resetError();

      await this.postLog('reject');

      this.isProcessing.reject = false;
    },
    postLog(choice) {
      const isNeed = choice === 'accept' ? true : false;
      const params = {
        date: this.$options.filters.dateText(utils.getDateResetHours()),
        type: this.proposal.type,
        keyword: this.proposal.keyword,
        url: this.proposal.url,
        isNeed: isNeed
      };
      if (this.proposal.type === 'seoCheckUp') {
        params.category = this.proposal.category;
        params.content = this.proposal.content;
      }
      return apiProjects.proposals.log
        .post(params, this.auth.idToken, this.project.id)
        .then(response => {
          if (choice === 'reject') {
            this.$emit('reject');
          }
        })
        .catch(error => {
          if (error.response && error.response.data) {
            this.errorResponse[choice] =
              error.response.data.errors[0].userMessage;
          } else {
            this.errorResponse[choice] = ERROR_MESSAGE.other;
          }
        });
    },
    getUsages() {
      const params = {
        items: 'tasks'
      };
      return apiProjects.usages
        .get(params, this.auth.idToken, this.project.id)
        .then(response => {
          const usages = response.data.items[0];
          const error = utils.getErrorUsages({
            usages: usages,
            target: 'タスク'
          });
          if (error.message) {
            this.errorResponse.accept = error.message;
          }
        })
        .catch(error => {
          if (error.response && error.response.data) {
            this.errorResponse.accept =
              error.response.data.errors[0].userMessage;
          } else {
            this.errorResponse.accept = ERROR_MESSAGE.other;
          }
        });
    },
    getUsers() {
      return this.$store.dispatch('project/getUsers').finally(() => {
        const existUser = this.projectUsers.some(
          user => user.value === this.params.operator
        );
        if (!existUser) {
          this.error.operator =
            '選択したユーザーが存在しません。担当者を再度設定してください';
        }
      });
    },
    postTask(proposal) {
      const taskDetail = utils.getProposalTaskDetail({
        content: proposal.content,
        keyword: proposal.keyword,
        type: proposal.type,
        url: proposal.url
      });
      const params = {
        column: 'todo',
        operatorUserId: this.params.operator || null,
        proposalId: proposal.id,
        taskDetail: taskDetail,
        type: proposal.type,
        isProposal: true
      };
      return apiProjects.tasks.siteAnalysis
        .post(params, this.auth.idToken, this.project.id)
        .then(response => {
          this.$store.dispatch('project/getTaskCount');
          this.$emit('accept');
        })
        .catch(error => {
          if (error.response && error.response.data) {
            this.errorResponse.accept =
              error.response.data.errors[0].userMessage;
          } else {
            this.errorResponse.accept = ERROR_MESSAGE.other;
          }
        });
    },
    resetError() {
      this.errorResponse.accept = '';
      this.errorResponse.reject = '';
    }
  }
};
</script>
